"use client";

import { RefreshCw } from "lucide-react";

import { cn } from "@/lib/utils";

import { SIGNUP_COUNTRY_PLACEHOLDER } from "@/features/auth/components/signup/signup-content";
import { countries } from "@/features/auth/data/countries";

/**
 * The signup-only presentational pieces: the country option list and the captcha
 * image + refresh control.
 *
 * The two primitives every auth screen needs — `FieldError` and `CheckRow` —
 * moved to `auth-fields` when `/login` grew a second caller for them.
 */

/** The <option> list for the country picker. Value = ISO code, as the existing
 * signup form and the backend both use. */
export function CountryOptions() {
  return (
    <>
      <option value="" disabled>
        {SIGNUP_COUNTRY_PLACEHOLDER}
      </option>
      {countries.map((country) => (
        <option
          key={country.country_id}
          value={country.country_code}
          className="bg-card text-foreground"
        >
          {country.country_name}
        </option>
      ))}
    </>
  );
}

/**
 * The challenge image plus its refresh control.
 *
 * The `*ClassName` props let a caller sit it in its own furniture without
 * duplicating the image/refresh wiring. Before the image resolves (it's drawn
 * after mount) the box holds its size, so nothing jumps.
 */
export function CaptchaImage({
  src,
  onRefresh,
  disabled,
  className,
  imageClassName,
  buttonClassName,
}: {
  src: string | null;
  onRefresh: () => void;
  disabled?: boolean;
  className?: string;
  imageClassName?: string;
  buttonClassName?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div
        className={cn(
          "h-[52px] w-[150px] shrink-0 overflow-hidden bg-card",
          imageClassName,
        )}
      >
        {src ? (
          /* A `data:` URL challenge at a fixed size — next/image can neither
           * optimise nor cache it, so a plain <img> is correct here. */
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={src}
            alt="Security code challenge"
            width={150}
            height={52}
            className="h-full w-full object-contain"
          />
        ) : null}
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={disabled}
        aria-label="Get a new security code"
        className={cn(
          "grid size-9 shrink-0 place-items-center text-muted-foreground",
          "transition-colors duration-150 hover:text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted-foreground",
          buttonClassName,
        )}
      >
        <RefreshCw aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
